<template>
    <input
        v-if="editable && field"
        class="price-tag-value__input"
        :class="{ 'price-tag-value__input--invalid': invalid }"
        :type="isNumeric ? 'number' : 'text'"
        :value="inputValue"
        :min="field.min"
        :max="field.max"
        :step="step"
        :maxlength="field.maxLength"
        :style="{ width: inputWidth }"
        :aria-label="label"
        @input="onInput"
    />
    <span v-else class="price-tag-value">{{ displayValue }}</span>
</template>

<script setup>
import { computed } from 'vue';
import { isNil } from 'lodash';
import priceTagFieldTypes from '@enums/price-tag-field-types';
import i18n from '@/js/vue-i18n';
import { usePriceTagText, PRICE_TAGS_I18N_BASE } from '@/js/composables/usePriceTagText';

const props = defineProps({
    field: {
        type: Object,
        default: null,
    },
    value: {
        type: [String, Number, Boolean],
        default: null,
    },
    editable: {
        type: Boolean,
        default: false,
    },
    invalid: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['update']);

const { formatMoney } = usePriceTagText();

const valueType = computed(() => (props.field ? props.field.type : props.type));

const isNumeric = computed(() =>
    [priceTagFieldTypes.money, priceTagFieldTypes.percent, priceTagFieldTypes.integer].includes(
        valueType.value
    )
);

const step = computed(() => (valueType.value === priceTagFieldTypes.money ? '0.01' : '1'));

const inputValue = computed(() => (isNil(props.value) ? '' : props.value));

const inputWidth = computed(() => `${Math.max(String(inputValue.value).length, 2) + 2}ch`);

const label = computed(() =>
    props.field ? i18n.global.t(`${PRICE_TAGS_I18N_BASE}.fields.${props.field.key}`) : ''
);

const displayValue = computed(() => {
    if (valueType.value === priceTagFieldTypes.money) return formatMoney(props.value);
    return isNil(props.value) || props.value === '' ? '–' : String(props.value);
});

const onInput = event => {
    const raw = event.target.value;
    if (!isNumeric.value) {
        emit('update', raw);
        return;
    }
    const parsed = raw === '' ? null : Number(raw);
    emit('update', Number.isNaN(parsed) ? null : parsed);
};
</script>

<style lang="scss" scoped>
.price-tag-value__input {
    font: inherit;
    color: inherit;
    text-transform: inherit;
    letter-spacing: inherit;
    background: rgba(255, 255, 255, 0.35);
    border: 0.2rem dashed currentColor;
    border-radius: 0.4rem;
    padding: 0 0.4rem;
    text-align: center;
    min-width: 3ch;
    max-width: 100%;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &:focus {
        outline: 0.2rem solid currentColor;
        background: rgba(255, 255, 255, 0.6);
    }

    &--invalid {
        border-color: #d50000;
        outline-color: #d50000;
    }
}
</style>
